/**
 * SpatchAI privacy filters — PRIVACY/SECURITY CRITICAL.
 *
 * Ported VERBATIM from the original index.html two-step questionnaire filter.
 * The redaction/extraction logic (every regex and every canonical option
 * string, including en-dash separators like 'Yes – minor children') must be
 * preserved exactly. Do not "clean up" or re-order the patterns.
 *
 *   STEP 1 — redactRawText: drops any line that labels or contains blocked PII
 *            (name, DOB, place of birth, address, employer name, spouse name).
 *   STEP 2 — extractPermittedFields: from the pre-redacted text, extracts ONLY
 *            the permitted CJQ fields.
 */

import type { Juror } from '@/types';

// ── STEP 1 OF 2: PRIVACY FILTER — redact blocked PII before any parsing ──────
// Operates on line level so partial redaction cannot leak a partial value.
// Returns cleaned text string (never stores or logs the removed content).
export function redactRawText(raw: string): string {
  if (!raw) return '';
  // Patterns for blocked field LABELS (the line that labels or contains the value)
  const blockedLabels = [
    // Name variants
    /^\s*(?:full\s*)?name\s*[:\-]/i,
    /^\s*(?:last|first|middle)\s*name\s*[:\-]/i,
    /^\s*juror\s*name\s*[:\-]/i,
    /^\s*applicant\s*name\s*[:\-]/i,
    // DOB / Date of Birth
    /^\s*(?:date\s*of\s*birth|dob|birth\s*date)\s*[:\-]/i,
    /^\s*born\s*(?:on|in)?\s*[:\-]?\s*\d/i,
    // Place of birth / birthplace
    /^\s*(?:place|city|country|state)\s*of\s*birth\s*[:\-]/i,
    /^\s*birthplace\s*[:\-]/i,
    /^\s*native\s*of\s*[:\-]/i,
    // Address variants
    /^\s*(?:home|current|mailing|residential)?\s*address\s*[:\-]/i,
    /^\s*street\s*(?:address)?\s*[:\-]/i,
    /^\s*city\s*[,:\-]\s*state/i,
    /^\s*zip(?:\s*code)?\s*[:\-]/i,
    /^\s*\d{1,5}\s+[a-z]{2,}\s+(?:st|ave|rd|blvd|ln|dr|ct|pl|way|circle)/i,
    // Employer / company name (label only — NOT business TYPE)
    /^\s*employer(?:\s*name)?\s*[:\-]/i,
    /^\s*company\s*name\s*[:\-]/i,
    /^\s*name\s*of\s*(?:employer|company|business)\s*[:\-]/i,
    // Spouse / partner / significant other
    /^\s*spouse(?:\s*name)?\s*[:\-]/i,
    /^\s*(?:husband|wife|partner)\s*(?:name)?\s*[:\-]/i,
    /^\s*name\s*of\s*spouse\s*[:\-]/i,
    // Spouse employer (blocked) — spouse age and education are kept
    /^\s*spouse(?:\'s)?\s*employer\s*[:\-]/i,
    /^\s*(?:husband|wife|partner)(?:\'s)?\s*(?:employer|company|workplace)\s*[:\-]/i,
    // Spouse city/state (blocked)
    /^\s*spouse(?:\'s)?\s*(?:city|state|address)\s*[:\-]/i,
    /^\s*significant\s*other\s*[:\-]/i,
  ];
  // Patterns for blocked VALUE content on any line (high-confidence PII formats)
  const blockedValues = [
    // Social Security — always block entire line
    /\b\d{3}[-\s]?\d{2}[-\s]?\d{4}\b/,
    // Full date of birth pattern (MM/DD/YYYY or YYYY-MM-DD)
    /\b(?:0?[1-9]|1[0-2])\/(?:0?[1-9]|[12]\d|3[01])\/(19|20)\d{2}\b/,
    /\b(19|20)\d{2}-(?:0?[1-9]|1[0-2])-(?:0?[1-9]|[12]\d|3[01])\b/,
    // Street address number + street name
    /\b\d{1,5}\s+[A-Za-z]{2,}\s+(?:Street|St|Avenue|Ave|Road|Rd|Boulevard|Blvd|Lane|Ln|Drive|Dr|Court|Ct|Place|Pl|Way|Circle|Terrace|Tr)\b/i,
  ];
  const lines = raw.split('\n');
  const kept: string[] = [];
  let dropped = 0;
  lines.forEach(function (line) {
    let skip = false;
    // Check label patterns
    for (let i = 0; i < blockedLabels.length; i++) {
      if (blockedLabels[i].test(line)) { skip = true; break; }
    }
    // Check value patterns (even if no label match)
    if (!skip) {
      for (let j = 0; j < blockedValues.length; j++) {
        if (blockedValues[j].test(line)) { skip = true; break; }
      }
    }
    if (skip) { dropped++; }
    else { kept.push(line); }
  });
  // Log count only — never log content of dropped lines
  if (dropped > 0) console.log('[SpatchAI Privacy] Redacted ' + dropped + ' line(s) matching blocked fields.');
  return kept.join('\n');
}

// ── FREE-TEXT SCRUB — for the questionnaire "describe" boxes ─────────────────
// The three describe boxes (experience-with-law, law-related-employment,
// impartiality) capture whatever the juror WROTE. redactRawText drops whole
// labeled lines, which is wrong here — we want to keep the substance and remove
// only the PII inside it. This replaces high-confidence PII SUBSTRINGS with
// "[redacted]". Names are best-effort: titled ("Mr. Smith") and explicit
// ("named John Smith") forms are caught; arbitrary names in prose cannot be
// detected without over-redacting ordinary capitalized words, so they are not.
const FREE_TEXT_PII: RegExp[] = [
  // Email address
  /\b[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}\b/g,
  // Social Security Number
  /\b\d{3}[-\s]\d{2}[-\s]\d{4}\b/g,
  // US phone number (various separators, optional country code / parens)
  /(?:\+?1[-.\s]?)?\(?\b\d{3}\)?[-.\s]\d{3}[-.\s]\d{4}\b/g,
  // Street address (number + street name + suffix)
  /\b\d{1,5}\s+[A-Za-z][A-Za-z.]*(?:\s+[A-Za-z.]+){0,3}\s+(?:Street|St|Avenue|Ave|Road|Rd|Boulevard|Blvd|Lane|Ln|Drive|Dr|Court|Ct|Place|Pl|Way|Circle|Cir|Terrace|Ter|Highway|Hwy)\b\.?/gi,
  // Full dates (MM/DD/YYYY or YYYY-MM-DD)
  /\b(?:0?[1-9]|1[0-2])\/(?:0?[1-9]|[12]\d|3[01])\/(?:19|20)\d{2}\b/g,
  /\b(?:19|20)\d{2}-(?:0?[1-9]|1[0-2])-(?:0?[1-9]|[12]\d|3[01])\b/g,
  // Titled names: Mr./Mrs./Ms./Dr./Prof. Lastname [Lastname]
  /\b(?:Mr|Mrs|Ms|Dr|Prof|Messrs)\.?\s+[A-Z][a-z]+(?:\s+[A-Z][a-z]+)?/g,
  // Explicit "named X" / "name is X"
  /\bnamed?\s+(?:is\s+)?[A-Z][a-z]+(?:\s+[A-Z][a-z]+)?/gi,
];

/**
 * Redact inline PII from a free-text questionnaire value, returning the cleaned
 * string. Safe on any input; empty/non-string returns unchanged.
 */
export function scrubFreeText(value: string): string {
  if (!value || typeof value !== 'string') return value;
  let out = value;
  for (const re of FREE_TEXT_PII) out = out.replace(re, '[redacted]');
  return out.replace(/\s{2,}/g, ' ').trim();
}

// ── STEP 2 OF 2: PRIVACY FILTER — extract ONLY permitted CJQ fields ──────────
// Receives pre-redacted text. Extracts only:
//   Jury #, Gender, Age range, Education, Employment status, Business type,
//   Occupation, Household size, Parent/guardian status, Jury service,
//   LE connection, Law-related employment, Crime victim, Accused, Impartiality
// BLOCKED (handled by redactRawText above): Name, DOB, Place of birth,
//   Address, Employer name, Spouse/partner name
//
// The original relied on a global `scanJurorCounter` for the auto-incremented
// panel number; here it is passed in explicitly as `panelNumber`.
export function extractPermittedFields(raw: string, panelNumber: number): Partial<Juror> {
  const j: Partial<Juror> = {};
  const lines = raw.split('\n').map(function (l) { return l.trim(); }).filter(Boolean);
  void lines;
  const text = raw.toLowerCase();

  // Jury number — use auto-counter (panelNumber), OCR number as fallback
  j.panelId = 'J-' + String(panelNumber).padStart(2, '0');
  const jnMatch = raw.match(/(?:juror\s*(?:no\.?|number|#)\s*:?\s*)(\w[\w-]*)/i) || raw.match(/(?:panel\s*(?:no\.?|number|#)\s*:?\s*)(\w[\w-]*)/i) || raw.match(/(?:^|\n)\s*(?:no\.|#)\s*(\d+)/im);
  void jnMatch;
  // Only override counter if a clear jury number found that doesn't conflict
  // Keep counter-based ID as primary

  // Gender / sex identity
  if (text.match(/\bfemale\b|\bwoman\b/)) j.gender = 'Female';
  else if (text.match(/\bmale\b|\bman\b/)) j.gender = 'Male';
  else if (text.match(/non.binary|non binary|genderqueer|gender fluid/)) j.gender = 'Non-binary';

  // Age — store exact integer; derive from DOB year if no explicit age field
  const ageMatch = raw.match(/(?:age|born)[^\d]*(\d{1,2})/i);
  if (ageMatch) { const a = parseInt(ageMatch[1]); if (a >= 18 && a < 100) { j.age = String(a); } }
  // DOB year pattern — compute exact age, do NOT store DOB
  const dobYear = raw.match(/(?:dob|date of birth|born)[^\d]*(?:\d{1,2}\/\d{1,2}\/)?(\d{4})/i) || raw.match(/(\d{4})[^\d]/);
  if (dobYear && !j.age) { const yr = parseInt(dobYear[1]); if (yr > 1920 && yr < 2010) { const calcAge = new Date().getFullYear() - yr; if (calcAge >= 18 && calcAge < 100) j.age = String(calcAge); } }

  // Education
  if (text.match(/phd|doctorate|doctoral/)) j.edu = 'PhD / Doctoral';
  else if (text.match(/j\.?d\.|law degree|juris/)) j.edu = 'JD / Law degree';
  else if (text.match(/m\.?d\.|medical degree/)) j.edu = 'MD / Medical';
  else if (text.match(/master|m\.?s\.|m\.?a\./)) j.edu = 'Master degree';
  else if (text.match(/bachelor|b\.?s\.|b\.?a\.|4.year college|four.year/)) j.edu = 'Bachelor degree';
  else if (text.match(/associate/)) j.edu = 'Associate degree';
  else if (text.match(/some college|technical|trade school|vocational/)) j.edu = 'Some college / trade school';
  else if (text.match(/high school|hs diploma|ged|graduated high/)) j.edu = 'HS diploma / GED';
  else if (text.match(/less than high|did not.*graduate/)) j.edu = 'Less than high school';

  // Employment status
  if (text.match(/self.employed|own.*business/)) j.empStatus = 'Self-employed';
  else if (text.match(/retired/)) j.empStatus = 'Retired';
  else if (text.match(/student/)) j.empStatus = 'Student';
  else if (text.match(/homemaker|stay.at.home/)) j.empStatus = 'Homemaker';
  else if (text.match(/unemployed|not.*employed|seeking work/)) j.empStatus = 'Unemployed / seeking';
  else if (text.match(/part.time/)) j.empStatus = 'Employed part-time';
  else if (text.match(/full.time|employed/)) j.empStatus = 'Employed full-time';

  // Business type — from industry keywords (NOT employer name)
  if (text.match(/hospital|clinic|medical|health care|healthcare/)) j.bizType = 'Healthcare / Medical';
  else if (text.match(/school|university|college|academic|educat/)) j.bizType = 'Education / Academic';
  else if (text.match(/law firm|attorney|legal|court|judicial/)) j.bizType = 'Legal / Law';
  else if (text.match(/police|law enforce|sheriff|correction|probation|parole/)) j.bizType = 'Law Enforcement / Corrections';
  else if (text.match(/military|army|navy|marines|air force|national guard/)) j.bizType = 'Military / Defense';
  else if (text.match(/government|federal|state.*employ|municipal|public sector/)) j.bizType = 'Government / Public sector';
  else if (text.match(/bank|financ|insurance|invest|accounting/)) j.bizType = 'Finance / Banking / Insurance';
  else if (text.match(/technolog|software|computer|it |information tech/)) j.bizType = 'Technology / IT';
  else if (text.match(/construct|trade|plumb|electric|mechanic|carpent/)) j.bizType = 'Trades / Construction / Labor';
  else if (text.match(/manufactur|industrial|factor/)) j.bizType = 'Manufacturing / Industrial';
  else if (text.match(/retail|sales|store|shop/)) j.bizType = 'Retail / Sales';
  else if (text.match(/restaurant|food|hospitality|hotel|catering/)) j.bizType = 'Food / Hospitality';
  else if (text.match(/transport|logistics|trucking|deliver/)) j.bizType = 'Transportation / Logistics';
  else if (text.match(/media|communicat|journal|publish|arts|entertain/)) j.bizType = 'Media / Communications / Arts';
  else if (text.match(/social service|nonprofit|non.profit|ngo/)) j.bizType = 'Social Services / Nonprofit';
  else if (text.match(/church|religious|clergy|pastor|minister|rabbi|imam/)) j.bizType = 'Religious / Clergy';

  // Occupation (job title from questionnaire occupation field — NOT employer name)
  const occMatch = raw.match(/(?:occupation|job title|position|employment)[^\n:]*:?\s*([A-Za-z][A-Za-z\s\/]{2,40})(?:\n|,|\.|employer)/i);
  if (occMatch) j.occ = occMatch[1].trim();

  // Household size
  const hhMatch = raw.match(/(?:household|people.*live|live.*with)[^\d]*(\d)/i) || raw.match(/household size[^\d]*(\d)/i);
  if (hhMatch) { const hh = parseInt(hhMatch[1]); j.hhSize = hh === 1 ? '1 (alone)' : hh === 2 ? '2' : hh === 3 ? '3' : hh === 4 ? '4' : '5+'; }

  // Parent / guardian of children
  if (text.match(/children[^\n]*(?:yes|have|guardian)|parent.*of.*minor|minor.*children/i)) j.parent = 'Yes – minor children';
  else if (text.match(/no.*children|no minor/i)) j.parent = 'No';

  // Spouse age range (kept) and spouse education (kept) — spouse NAME and employer stripped by redactor
  const spouseAgeMatch = raw.match(/(?:spouse|husband|wife|partner)[^\n]*(?:age|born)[^\d]*(\d{1,2})/i);
  if (spouseAgeMatch) { const sa = parseInt(spouseAgeMatch[1]); if (sa >= 18 && sa < 100) { j.spouseAge = sa < 25 ? '18-24' : sa < 35 ? '25-34' : sa < 45 ? '35-44' : sa < 55 ? '45-54' : sa < 65 ? '55-64' : sa < 75 ? '65-74' : '75+'; } }
  const spouseEduText = text.match(/(?:spouse|husband|wife|partner)[^\n]*(education|degree|school|college)[^\n]*/i);
  if (spouseEduText) {
    const se = spouseEduText[0];
    if (se.match(/phd|doctorate/)) j.spouseEdu = 'PhD / Doctoral';
    else if (se.match(/j\.?d\.|law degree/)) j.spouseEdu = 'JD / Law degree';
    else if (se.match(/master|m\.?s\.|m\.?a\./)) j.spouseEdu = 'Master degree';
    else if (se.match(/bachelor|b\.?s\.|b\.?a\.|college/)) j.spouseEdu = 'Bachelor degree';
    else if (se.match(/associate/)) j.spouseEdu = 'Associate degree';
    else if (se.match(/some college|trade|technical/)) j.spouseEdu = 'Some college / trade school';
    else if (se.match(/high school|hs|ged/)) j.spouseEdu = 'HS diploma / GED';
  }

  // Prior jury service
  if (text.match(/(?:jury.*service|served.*jury|prior.*jury).*(?:criminal|felony)/i)) j.jury = 'Yes – criminal trial';
  else if (text.match(/(?:jury.*service|served.*jury|prior.*jury).*civil/i)) j.jury = 'Yes – civil trial';
  else if (text.match(/(?:jury.*service|served.*jury|prior.*jury).*both/i)) j.jury = 'Yes – both civil and criminal';
  else if (text.match(/grand jury/i)) j.jury = 'Grand jury';
  else if (text.match(/(?:jury.*service|prior.*jury)[^\n]*(?:yes|have served)/i)) j.jury = 'Served, type unknown';
  else if (text.match(/(?:jury.*service|prior.*jury)[^\n]*(?:no|never|have not)/i)) j.jury = 'No';

  // LE connection — relationship only, NOT name
  if (text.match(/law enforce|police|sheriff|correction|probation|trooper/)) {
    if (text.match(/self|yourself|are you/i)) j.le = 'Self – active LE';
    else if (text.match(/spouse|husband|wife|partner/i)) j.le = 'Spouse / partner – active LE';
    else if (text.match(/parent|mother|father|mom|dad/i)) j.le = 'Parent – active or retired LE';
    else if (text.match(/sibling|brother|sister/i)) j.le = 'Sibling – active or retired LE';
    else if (text.match(/child|son|daughter/i)) j.le = 'Child – active or retired LE';
    else if (text.match(/friend|relative|family|cousin|uncle|aunt/i)) j.le = 'Extended family – LE';
    else j.le = 'Extended family – LE';
  } else { j.le = 'None'; }

  // Law-related employment
  if (text.match(/attorney|lawyer\b/i)) j.lawEmp = 'Attorney / Lawyer';
  else if (text.match(/paralegal|legal assistant/i)) j.lawEmp = 'Paralegal / Legal assistant';
  else if (text.match(/law clerk|judicial clerk/i)) j.lawEmp = 'Law clerk / Judge\'s staff';
  else if (text.match(/court officer|clerk of court/i)) j.lawEmp = 'Court officer / Clerk';
  else if (text.match(/correction officer|co \b/i)) j.lawEmp = 'Corrections officer';
  else if (text.match(/probation|parole officer/i)) j.lawEmp = 'Probation / Parole officer';
  else if (text.match(/forensic|crime lab|dna analyst/i)) j.lawEmp = 'Forensic professional';
  else j.lawEmp = 'None';

  // Crime victim status
  if (text.match(/victim[^\n]*(?:violent|assault|murder|robbery|attack)/i)) j.victim = 'Yes – violent crime';
  else if (text.match(/victim[^\n]*(?:property|theft|burglary|vandal)/i)) j.victim = 'Yes – property crime';
  else if (text.match(/victim[^\n]*(?:fraud|financ|scam)/i)) j.victim = 'Yes – fraud / financial crime';
  else if (text.match(/victim[^\n]*(?:sexual|rape)/i)) j.victim = 'Yes – sexual assault';
  else if (text.match(/victim[^\n]*(?:dui|drunk driving)/i)) j.victim = 'Yes – DUI-related';
  else if (text.match(/crime victim[^\n]*yes|been.*victim.*crime/i)) j.victim = 'Yes – other';
  else if (text.match(/crime victim[^\n]*no|not.*victim/i)) j.victim = 'No';

  // Accused / charged
  if (text.match(/(?:you|family).*(?:charged|arrested|convicted|accused)/i)) {
    if (text.match(/self|yourself|i have/i)) j.accused = 'Yes – self, charged or convicted';
    else j.accused = 'Yes – family member charged or convicted';
  } else { j.accused = 'No'; }

  // Impartiality
  if (text.match(/(?:fair|impartial)[^\n]*(?:yes|can be|able to)/i)) j.fair = 'Yes – stated can be fair';
  else if (text.match(/(?:fair|impartial)[^\n]*(?:no|cannot|not able|not sure|uncertain)/i)) j.fair = 'Uncertain – qualified answer';

  // Case knowledge
  if (text.match(/heard.*case|know.*case|familiar.*case|read.*about/i)) j.caseKnow = 'Heard of case – media';
  else j.caseKnow = 'None stated';

  j.opinion = 'None stated';
  return j;
}
